/**
 * App.tsx — extracted verbatim from bishopric-complete/index.html during M1 migration.
 *
 * This file is intentionally un-type-checked and un-linted. Milestone 2 will
 * break it apart into per-tab modules with proper types. Do not edit surgically
 * here; instead, extract the section you want to change into its own module
 * first.
 *
 * Original source: the <script type="text/babel"> block in index.html,
 * lines 371-13848. The block assumes globals set up by the Firebase init
 * module in index.html (window.firebaseAuth, window.firestoreDoc, etc.).
 */
import React from 'react'
import * as ReactDOM from 'react-dom/client'
import LandingPage from './shared/LandingPage'
import PullToRefreshIndicator from './shared/ui/PullToRefreshIndicator'
import { lazyWithRetry, isChunkLoadError, reloadForStaleChunk } from './shared/lib/lazyWithRetry'
// Ward tabs + StakeAdminView are lazy-loaded so the initial bundle ships
// only what a signed-out / ward-home user needs. Each of these becomes its
// own chunk, fetched when the tab is first visited. Ward users never download
// the stake bundle; stake users never download the ward tabs.
const NotesTab = lazyWithRetry(() => import('./ward/NotesTab'))
const WardDashboard = lazyWithRetry(() => import('./ward/WardDashboard'))
const WardOnboardingWizard = lazyWithRetry(() => import('./ward/WardOnboardingWizard'))
const WardActionItemsView = lazyWithRetry(() => import('./ward/WardActionItemsView'))
const MembersTab = lazyWithRetry(() => import('./ward/MembersTab'))
const TrackingTab = lazyWithRetry(() => import('./ward/TrackingTab'))
// 2026-05 cutover: the Callings tab points at WardCallingsV2View.
// Legacy ward V1 components (CallingsTab, WardCallingsView) stay on disk
// under apps/web/src/ward/ but are no longer imported here.
const WardCallingsV2View = lazyWithRetry(() => import('./ward/WardCallingsV2View'))
// Read-only view over the stake's wardOrdinations projection (this ward only).
const WardOrdinationsView = lazyWithRetry(() => import('./ward/WardOrdinationsView'))
const WardAaronicOrdinationsView = lazyWithRetry(
  () => import('./ward/WardAaronicOrdinationsView'),
)
const InterviewsTab = lazyWithRetry(() => import('./ward/InterviewsTab'))
// Phase 3: ward meetings tab now uses the unified StakeMeetingsTab (a renaming
// pass to drop the "Stake" prefix is queued). The component is org-agnostic;
// ward-flavored props (bishopric members, ward roles, ward-typed leader
// options) flow through.
const MeetingsTab = lazyWithRetry(() => import('./stake/StakeMeetingsTab'))
const SacramentTab = lazyWithRetry(() => import('./ward/SacramentTab'))
const _AgendaEditor = lazyWithRetry(() => import('./ward/AgendaEditor'))
const _AgendaViewer = lazyWithRetry(() => import('./ward/AgendaViewer'))
const SettingsTab = lazyWithRetry(() => import('./ward/SettingsTab'))
const StakeAdminView = lazyWithRetry(() => import('./stake/StakeAdminView'))
// Missionary Work — its own page at /missionary, and the one route that
// renders for signed-OUT visitors too (the member half is deliberately
// login-free). Lazy so its bundle + design CSS never load for anyone else.
const MissionaryPage = lazyWithRetry(() => import('./missionary/MissionaryPage'))
// 40-Day Fast — also its own linkable URL, reached from a text message or the
// QR code on a printed flier. Anonymous only: there are no leader tools on it,
// so unlike /missionary it takes no session props.
const FastPage = lazyWithRetry(() => import('./fast/FastPage'))
import OrgSelectScreen from './auth/OrgSelectScreen'
const SuperAdminPage = lazyWithRetry(() => import('./auth/SuperAdminPage'))
import FloatingTabBar from './shared/ui/FloatingTabBar'
import ConnectionBanner from './shared/ui/ConnectionBanner'
import AppSidebar, {
  formatWardBrand,
  type NavItem,
  type NavSection,
} from './shared/ui/AppSidebar'
import LoadingFallback from './shared/ui/LoadingFallback'
import { allHymns } from './shared/lib/hymns'
import { isMissionaryInWard } from '@leadership/types'
import { createDb } from './shared/lib/db'
import { clearSavedOrgId } from './shared/lib/orgStorage'
import { EMPTY_PRAYER_LISTS, type PrayerListsBlob } from '@leadership/types'
import {
  normalizePrayerListsBlob,
  savePrayerLists as savePrayerListsBlob,
} from './stake/lib/prayerLists'

const { useState, useEffect, useRef } = React

const WARD_SIDEBAR_COLLAPSED_STORAGE_KEY = 'wardSidebarCollapsed'

const WARD_DASHBOARD: NavItem = { id: 'home', label: 'Home', icon: 'home-outline' }

const WARD_SECTIONS: NavSection[] = [
  {
    title: 'On the calendar',
    items: [
      { id: 'meetings', label: 'Meetings', icon: 'calendar-outline' },
      { id: 'assignments', label: 'Action items', icon: 'checkmark-done-outline' },
      { id: 'interviews', label: 'Interviews', icon: 'chatbubbles-outline' },
      { id: 'sacrament', label: 'Sacrament Meeting', icon: 'book-outline' },
    ],
  },
  {
    title: 'Positions',
    items: [
      { id: 'callings', label: 'Callings', icon: 'people-outline' },
      // Read-only view over the stake's wardOrdinations projection, filtered
      // to this ward. Ordinations are run at the stake; wards just track.
      { id: 'ordinations', label: 'Ordinations', icon: 'ribbon-outline' },
    ],
  },
  {
    title: 'Ward',
    items: [
      { id: 'members', label: 'Members', icon: 'people-circle-outline' },
      { id: 'tracking', label: 'Tracking', icon: 'bar-chart-outline' },
      { id: 'notes', label: 'Notes', icon: 'document-text-outline' },
    ],
  },
]

const WARD_SETTINGS: NavItem = { id: 'settings', label: 'Settings', icon: 'settings-outline' }

const _ACCOUNT_ROLES = ['bishop', 'counselor1', 'counselor2', 'clerk', 'exec_secretary', 'test']
const _ROLE_DISPLAY_NAMES = {
  bishop: 'Bishop',
  counselor1: '1st Counselor',
  counselor2: '2nd Counselor',
  clerk: 'Clerk',
  exec_secretary: 'Executive Secretary',
  test: 'Test User',
}

// Phase 3: pull the canonical ward role labels + edit predicate from the
// shared package so they stay in sync with the meetings code.
import { ROLE_DISPLAY_NAMES, canEditWard } from '@leadership/roles'

// Tenant detection now happens entirely after sign-in via the
// OrgSelectScreen + /my-organizations + per-UID localStorage flow,
// matching the iOS app. The previous subdomain-based path
// (`getSubdomain()` / `resolveOrganization()`) was removed in Phase A
// of the multi-tenant migration along with its backend counterpart
// (`GET /tenant/resolve` + the `resolveTenant` middleware). Old
// per-ward subdomains keep working by redirecting to the apex domain
// with an `?org=<id>` query param, which OrgSelectScreen reads on
// mount.

// Pull-to-refresh hook for mobile
function usePullToRefresh() {
  const [pulling, setPulling] = useState(false)
  const [pullDistance, setPullDistance] = useState(0)
  const startY = useRef(0)
  const isPulling = useRef(false)
  const pullDistanceRef = useRef(0)
  const threshold = 80

  useEffect(() => {
    const onTouchStart = (e) => {
      if (window.scrollY === 0) {
        startY.current = e.touches[0].clientY
        isPulling.current = true
      }
    }
    const onTouchMove = (e) => {
      if (!isPulling.current) return
      const dy = e.touches[0].clientY - startY.current
      if (dy > 0 && window.scrollY === 0) {
        const dist = Math.min(dy * 0.5, 120)
        pullDistanceRef.current = dist
        setPullDistance(dist)
        setPulling(true)
        if (dy > 10) e.preventDefault()
      } else {
        isPulling.current = false
        pullDistanceRef.current = 0
        setPulling(false)
        setPullDistance(0)
      }
    }
    const onTouchEnd = () => {
      if (isPulling.current && pullDistanceRef.current >= threshold) {
        window.location.reload()
      }
      isPulling.current = false
      pullDistanceRef.current = 0
      setPulling(false)
      setPullDistance(0)
    }
    document.addEventListener('touchstart', onTouchStart, { passive: true })
    document.addEventListener('touchmove', onTouchMove, { passive: false })
    document.addEventListener('touchend', onTouchEnd, { passive: true })
    return () => {
      document.removeEventListener('touchstart', onTouchStart)
      document.removeEventListener('touchmove', onTouchMove)
      document.removeEventListener('touchend', onTouchEnd)
    }
  }, [])

  return { pulling, pullDistance, threshold }
}

// Error boundary to catch React render errors
interface ErrorBoundaryProps {
  children?: React.ReactNode
}
interface ErrorBoundaryState {
  error: Error | null
}
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { error: null }
  }
  static getDerivedStateFromError(error: Error) {
    return { error }
  }
  componentDidCatch(error: Error, info: unknown) {
    console.error('[ErrorBoundary]', error, info)
    // A stale-chunk error means a new version deployed while this tab was
    // open. Reload to the fresh shell (self-guarded against loops); if the
    // reload is declined, render() falls back to a calm reload prompt
    // rather than a raw stack trace.
    if (isChunkLoadError(error)) reloadForStaleChunk()
  }
  render() {
    const { error } = this.state
    if (!error) return this.props.children

    // Stale chunk after a deploy: show a calm "updating" card, not a scary
    // stack trace. componentDidCatch has usually already kicked off an
    // automatic reload by now; the button is the manual fallback.
    if (isChunkLoadError(error))
      return React.createElement(
        'div',
        { style: { padding: '40px', textAlign: 'center', fontFamily: 'sans-serif' } },
        React.createElement(
          'h2',
          { style: { color: '#0f172a' } },
          'Updating to the latest version…',
        ),
        React.createElement(
          'p',
          { style: { color: '#475569', maxWidth: '460px', margin: '12px auto 20px' } },
          'A new version of the app was just released. This should refresh ' +
            'automatically — if it doesn’t, tap Reload.',
        ),
        React.createElement(
          'button',
          {
            onClick: () => window.location.reload(),
            style: {
              color: '#fff',
              background: '#2196b8',
              fontWeight: 600,
              padding: '10px 20px',
              borderRadius: '8px',
              cursor: 'pointer',
              border: 'none',
              fontSize: '16px',
            },
          },
          'Reload',
        ),
      )

    // Genuine render error: keep the diagnostic view (message + stack).
    return React.createElement(
      'div',
      { style: { padding: '40px', textAlign: 'center', fontFamily: 'sans-serif' } },
      React.createElement('h2', { style: { color: '#e11d48' } }, 'Render Error'),
      React.createElement(
        'pre',
        {
          style: {
            textAlign: 'left',
            background: '#f1f5f9',
            padding: '16px',
            borderRadius: '8px',
            maxWidth: '600px',
            margin: '16px auto',
            overflow: 'auto',
            fontSize: '13px',
            whiteSpace: 'pre-wrap',
          },
        },
        `${error.message}\n\n${error.stack || ''}`,
      ),
      React.createElement(
        'button',
        {
          onClick: () => this.setState({ error: null }),
          style: {
            color: '#2196b8',
            fontWeight: 600,
            marginTop: '16px',
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            fontSize: '16px',
          },
        },
        'Try Again',
      ),
    )
  }
}

function App() {
  const [user, setUser] = useState(null)
  // Path-based routing for the one page that has a URL of its own. The app is
  // otherwise a single state-driven SPA, so this is a deliberate special case
  // rather than the start of a router: /missionary has to be linkable, because
  // members reach it from a text message, not from inside the app.
  const [isMissionaryRoute, setIsMissionaryRoute] = useState(
    () => window.location.pathname.replace(/\/+$/, '') === '/missionary',
  )
  const [isFastRoute, setIsFastRoute] = useState(
    () => window.location.pathname.replace(/\/+$/, '') === '/fast',
  )
  useEffect(() => {
    const sync = () => {
      const path = window.location.pathname.replace(/\/+$/, '')
      setIsMissionaryRoute(path === '/missionary')
      setIsFastRoute(path === '/fast')
    }
    window.addEventListener('popstate', sync)
    return () => window.removeEventListener('popstate', sync)
  }, [])
  const [authError, setAuthError] = useState('')
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('home')
  const [menuOpen, setMenuOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(WARD_SIDEBAR_COLLAPSED_STORAGE_KEY) === '1'
    } catch {
      return false
    }
  })
  const toggleSidebarCollapsed = React.useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev
      try {
        localStorage.setItem(WARD_SIDEBAR_COLLAPSED_STORAGE_KEY, next ? '1' : '0')
      } catch {}
      return next
    })
  }, [])
  const [agendas, setAgendas] = useState({ bishopric: [], wardCouncil: [] })
  const [callings, setCallings] = useState([])
  // Workflow-based replacement for the legacy `callings` state. Lives on
  // its own Firestore key (`data/callingWorkflows`) and on the new
  // WardCallingsView screen. The legacy `callings` field stays around
  // until the per-org "Convert legacy callings" migration runs (C5),
  // after which the callings array is cleared.
  // (V1 callingWorkflows/callingWorkflowConfig state removed in the callings-v2
  // cleanup — abandoned; the orphaned Firestore blobs are left untouched.)
  // V2 callings (2026-05 redesign) — parallel storage so the legacy
  // callingWorkflows blob stays untouched during the pilot.
  const [callingWorkflowsV2, setCallingWorkflowsV2] = useState<any[]>([])
  const [callingsV2Config, setCallingsV2Config] = useState<any>({})
  const [memberList, setMemberList] = useState([])
  const [sacramentTracking, setSacramentTracking] = useState([])
  const [interviews, setInterviews] = useState([])
  const [orgTodos, setOrgTodos] = useState([])
  const [orgActionItems, setOrgActionItems] = useState([])
  const [sacramentAgendas, setSacramentAgendas] = useState([])
  const [settings, setSettings] = useState({})
  const [wardInfo, setWardInfo] = useState({})
  const [fetchedHymns, setFetchedHymns] = useState(null)
  const [customHymns, setCustomHymns] = useState([])
  // Centrally-managed custom hymns inherited from the parent stake (ward
  // orgs only). Populated by the effect that resolves the ward's stakeId.
  const [stakeCustomHymns, setStakeCustomHymns] = useState<any[]>([])
  // Stake→ward sharing projections (ward orgs only) — display-only docs the
  // wardShares Cloud Function triggers maintain on the stake org. Same
  // cross-org subscribe as stakeCustomHymns.
  //   • wardSharedMeetings: { schedules, meetings } — stake meetings flagged
  //     to also appear on ward meeting pages (read-only there).
  //   • wardOrdinations: in-progress ordinations, minimal display fields;
  //     filtered to THIS ward before rendering.
  const [stakeSharedMeetings, setStakeSharedMeetings] = useState<any>(null)
  const [stakeWardOrdinations, setStakeWardOrdinations] = useState<any[]>([])
  // Stake's departing/returning missionaries, PII-stripped (data/wardMissionaries
  // projection). Filtered to THIS ward before feeding the calendar's depart/
  // return events. Same cross-org subscribe as the projections above.
  const [stakeWardMissionaries, setStakeWardMissionaries] = useState<any[]>([])
  // Phase 3: unified meetings state, array shape. Replaces the legacy
  // `meetings` (MeetingsData dict) which was deleted in PR #6 along with
  // the ward MeetingsTab/MeetingTypeView consumers.
  const [meetingsList, setMeetingsList] = useState<any[]>([])
  const [meetingSchedules, setMeetingSchedules] = useState<any>({})
  // Ward-level prayer list. Single roster per ward, stored at
  // organizations/{wardId}/data/prayerLists. The underlying blob shape
  // supports two lists (presidency/council) for parity with stake; wards
  // use only the 'council' slice by convention — the WardPrayerListCard
  // hides the second list. Edits propagate live to every meeting whose
  // agenda has a prayer_list slot.
  const [prayerLists, setPrayerLists] = useState<PrayerListsBlob>(EMPTY_PRAYER_LISTS)
  const [meetingTemplates, setMeetingTemplates] = useState<
    import('@leadership/types').MeetingTemplatesBlob
  >({ templates: [] })
  const [notes, setNotes] = useState([])
  const [subscriptions, setSubscriptions] = useState([])
  // Multi-tenancy state. `organization` stays null until the user has
  // signed in *and* picked one in OrgSelectScreen (or auto-selection
  // resolved a single org / a saved-per-UID preference). `db` is the
  // per-org Firestore handle; reset on every sign-out so the next
  // session starts clean.
  const [organization, setOrganization] = useState(null)
  // Super-admin route flag. Lives in App.tsx state because the page
  // sits at the same level as OrgSelectScreen — both render before
  // an org is picked. The link on OrgSelectScreen calls
  // setShowSuperAdmin(true); the page's onBack flips it back.
  const [showSuperAdmin, setShowSuperAdmin] = useState(false)
  const [orgUsers, setOrgUsers] = useState([])
  const [currentUserRole, setCurrentUserRole] = useState(null)
  const [db, setDb] = useState(null)
  // New-ward onboarding. `onboardingEnabled` is the admin flag read from the
  // ward's own org doc (not a data blob); the wizard open/step state drives
  // the guided modal launched from the dashboard checklist card.
  const [onboardingEnabled, setOnboardingEnabled] = useState(false)
  const [onboardingOpen, setOnboardingOpen] = useState(false)
  const [onboardingStepId, setOnboardingStepId] = useState<string | null>(null)
  // Number of orgs the signed-in user can access, captured from
  // /my-organizations when OrgSelectScreen mounts. We use it only to
  // decide whether the "Switch Organization" affordance is worth
  // showing — for a single-org user, the button is a no-op (it would
  // bounce them through the picker right back into the same org), so
  // we hide it. Stays at 0 until OrgSelectScreen has loaded once.
  const [myOrgCount, setMyOrgCount] = useState(0)
  // `isStakeView` derives from the picked org's type — when stake
  // view is active, App renders <StakeAdminView/> instead of the ward
  // tab stack below.
  const isStakeView = organization?.type === 'stake'
  const canSwitchOrg = myOrgCount > 1

  // Pull-to-refresh — must be called before any early returns (Rules of Hooks)
  const { pulling, pullDistance, threshold } = usePullToRefresh()

  // Deep-link targets — set by navigateTo when an opts payload arrives
  // (e.g. clicking a meeting on the dashboard). Each downstream tab reads
  // its prop, opens the matching detail/view, and calls the consume
  // callback to clear the pending id.
  const [pendingMeetingId, setPendingMeetingId] = useState<string | null>(null)
  const [pendingSacramentId, setPendingSacramentId] = useState<string | null>(null)
  const [pendingWorkflowId, setPendingWorkflowId] = useState<string | null>(null)
  // Schedule deep-link: opens the meetings tab straight into a specific
  // schedule's recurrence/time editor (used by the onboarding wizard).
  const [pendingScheduleKey, setPendingScheduleKey] = useState<string | null>(null)
  // Bumped on every plain left-nav click (a navigateTo with no deep-link
  // target). Tabs that hold an internal detail/sub-view watch it to drop back
  // to their main list, so a left-nav item always lands on its list page.
  const [navListNonce, setNavListNonce] = useState(0)

  // SPA history navigation — push state on tab change so back button returns to previous tab
  const navigateTo = (tab: string, opts: any = {}) => {
    setActiveTab(tab)
    setMenuOpen(false)
    if (opts?.meetingId) setPendingMeetingId(String(opts.meetingId))
    if (opts?.sacramentId != null) setPendingSacramentId(String(opts.sacramentId))
    if (opts?.workflowId) setPendingWorkflowId(String(opts.workflowId))
    if (opts?.scheduleKey) setPendingScheduleKey(String(opts.scheduleKey))
    // No deep-link target → this is a plain left-nav click; tell tabs to
    // return to their list view (deep-links keep their target open instead).
    if (
      !opts?.meetingId &&
      opts?.sacramentId == null &&
      !opts?.workflowId &&
      !opts?.scheduleKey
    ) {
      setNavListNonce((n) => n + 1)
    }
    // A settingsSection opt lands the Settings tab on a specific panel:
    // SettingsShell reads window.location.hash at mount, so fold it into the
    // history push (no extra entry, no hashchange).
    window.history.pushState(
      { tab },
      '',
      opts?.settingsSection ? '#' + opts.settingsSection : '',
    )
    window.scrollTo({ top: 0, left: 0 })
  }

  useEffect(() => {
    // Set initial history state
    window.history.replaceState({ tab: 'home' }, '')

    const handlePopState = (e) => {
      if (e.state?.tab) {
        setActiveTab(e.state.tab)
        setMenuOpen(false)
      }
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  // Auth-only listener. Org loading happens later, after the user
  // picks an org in OrgSelectScreen (or auto-selection resolves
  // one). Sign-out clears every org-bound piece of state so the
  // next sign-in starts fresh — without this, signing out in account
  // A and signing in as account B would briefly show A's data.
  useEffect(() => {
    let cancelled = false
    const authUnsub = window.onAuthStateChanged(window.firebaseAuth, (u) => {
      if (cancelled) return
      setUser(u)
      if (!u) {
        setOrganization(null)
        setDb(null)
        setOrgUsers([])
        setCurrentUserRole(null)
        setMyOrgCount(0)
        for (const unsub of subscriptions) unsub()
        setSubscriptions([])
        resetOrgScopedState()
        setAuthError('')
      }
      setLoading(false)
    })
    return () => {
      cancelled = true
      authUnsub()
      for (const unsub of subscriptions) unsub()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Set dynamic page title based on the selected org. When no org is
  // picked yet (sign-in or org-picker visible) the static title from
  // index.html stays.
  useEffect(() => {
    if (organization?.name) document.title = organization.name
  }, [organization])

  useEffect(() => {
    const h = (e) => saveMemberList(e.detail)
    window.addEventListener('updateMembers', h)
    return () => window.removeEventListener('updateMembers', h)
  }, [db])

  const setupRealtimeListeners = (orgDb, currentUser = user) => {
    // Clean up any existing subscriptions
    for (const unsub of subscriptions) unsub()
    const _uid = currentUser?.uid || user?.uid
    const unsubs = [
      orgDb.subscribe('agendas', (v) => {
        if (v) setAgendas(v)
      }),
      orgDb.subscribe('callings', (v) => {
        if (v) setCallings(v)
      }),
      // (V1 callingWorkflows / callingWorkflowConfig subscriptions removed —
      // abandoned in the callings-v2 cleanup; nothing reads them now.)
      // V2 callings — separate Firestore keys so the legacy data is
      // safe and the new pilot can be deleted/reset without disturbing
      // anything else.
      orgDb.subscribe('callingWorkflowsV2', (v) => {
        if (Array.isArray(v)) setCallingWorkflowsV2(v)
      }),
      orgDb.subscribe('callingsV2Config', (v) => {
        if (v && typeof v === 'object') setCallingsV2Config(v)
      }),
      orgDb.subscribe('memberList', (v) => {
        if (v) setMemberList(v)
      }),
      orgDb.subscribe('sacramentTracking', (v) => {
        if (v) setSacramentTracking(v)
      }),
      orgDb.subscribe('interviews', (v) => {
        if (v) setInterviews(v)
      }),
      orgDb.subscribe('todos', (v) => {
        if (v) setOrgTodos(v)
      }),
      orgDb.subscribe('actionItems', (v) => {
        if (v) setOrgActionItems(v)
      }),
      orgDb.subscribe('sacramentAgendas', (v) => {
        if (v) setSacramentAgendas(v)
      }),
      orgDb.subscribe('settings', (v) => {
        if (v) setSettings(v)
      }),
      orgDb.subscribe('wardInfo', (v) => {
        if (v) setWardInfo(v)
      }),
      orgDb.subscribe('hymns', (v) => {
        if (v && Array.isArray(v) && v.length > 0) setFetchedHymns(v)
      }),
      orgDb.subscribe('customHymns', (v) => {
        if (v && Array.isArray(v)) setCustomHymns(v)
      }),
      orgDb.subscribe('meetings', (v) => {
        if (v && Array.isArray(v)) setMeetingsList(v)
      }),
      orgDb.subscribe('meetingSchedules', (v) => {
        if (v) setMeetingSchedules(v)
      }),
      orgDb.subscribe('meetingTemplates', (v) => {
        if (v && Array.isArray(v.templates)) setMeetingTemplates(v)
      }),
      orgDb.subscribe('prayerLists', (v: unknown) => {
        setPrayerLists(normalizePrayerListsBlob(v))
      }),
      orgDb.subscribe('notes', (v) => {
        if (v) setNotes(v)
      }),
    ]
    setSubscriptions(unsubs)
  }

  // Reconnect real-time listeners when app returns from background (critical for iOS PWA)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && db) {
        console.log('App resumed — reconnecting real-time listeners')
        setupRealtimeListeners(db)
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [db])

  // Ward orgs inherit stake-published docs: centrally-managed custom hymns
  // plus the ward-share projections (shared meetings + ordinations). The org
  // object from /my-organizations doesn't carry stakeId, so resolve it from
  // the ward's own org doc (member-readable), then live-subscribe to the
  // stake's docs. Re-resolved whenever the active org changes.
  // Read the ward's onboarding flag from its own (member-readable) org doc.
  // `onboardingEnabled` is an admin-controlled provisioning flag on the org
  // document (sibling of `isDemo`), not a data blob — so it isn't part of the
  // realtime blob subscriptions. Re-read whenever the active ward changes.
  useEffect(() => {
    setOnboardingEnabled(false)
    if (organization?.type !== 'ward' || !organization?.id) return
    let cancelled = false
    ;(async () => {
      try {
        const snap = await window.firestoreGetDoc(
          window.firestoreDoc(window.firebaseDb, 'organizations', organization.id),
        )
        if (!cancelled && snap.exists()) {
          setOnboardingEnabled(snap.data().onboardingEnabled === true)
        }
      } catch (e) {
        console.warn('Could not read ward onboarding flag:', e)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [organization?.id, organization?.type])

  useEffect(() => {
    setStakeCustomHymns([])
    setStakeSharedMeetings(null)
    setStakeWardOrdinations([])
    setStakeWardMissionaries([])
    if (organization?.type !== 'ward') return
    const unsubs: Array<() => void> = []
    let cancelled = false
    ;(async () => {
      let stakeId = organization.stakeId
      if (!stakeId) {
        try {
          const snap = await window.firestoreGetDoc(
            window.firestoreDoc(window.firebaseDb, 'organizations', organization.id),
          )
          stakeId = snap.exists() ? snap.data().stakeId || null : null
        } catch (e) {
          console.warn('Could not resolve parent stakeId for stake-doc inheritance:', e)
        }
      }
      if (cancelled || !stakeId) return
      const stakeDb = createDb(stakeId)
      unsubs.push(
        stakeDb.subscribe('customHymns', (v: any) => {
          setStakeCustomHymns(Array.isArray(v) ? v : [])
        }),
      )
      unsubs.push(
        stakeDb.subscribe('wardSharedMeetings', (v: any) => {
          setStakeSharedMeetings(v && typeof v === 'object' ? v : null)
        }),
      )
      unsubs.push(
        stakeDb.subscribe('wardOrdinations', (v: any) => {
          setStakeWardOrdinations(Array.isArray(v) ? v : [])
        }),
      )
      unsubs.push(
        stakeDb.subscribe('wardMissionaries', (v: any) => {
          setStakeWardMissionaries(Array.isArray(v) ? v : [])
        }),
      )
    })()
    return () => {
      cancelled = true
      for (const unsub of unsubs) {
        try {
          unsub()
        } catch {}
      }
    }
  }, [organization?.id, organization?.type, organization?.stakeId])

  const _saveAgendas = async (v) => {
    setAgendas(v)
    if (db) await db.set('agendas', v)
  }
  const saveCallings = async (v) => {
    setCallings(v)
    if (db) await db.set('callings', v)
  }
  // saveCallingWorkflows (V1 write helper) removed in the callings-v2 cleanup —
  // the only writer (WardCallingsView) is gone. The `callingWorkflows` read
  // subscription + state stay until Phase 2 re-points the live readers at V2.
  const saveCallingWorkflowsV2 = async (v: any[]) => {
    setCallingWorkflowsV2(v)
    if (db) await db.set('callingWorkflowsV2', v)
  }
  const saveCallingsV2Config = async (v: any) => {
    setCallingsV2Config(v)
    if (db) await db.set('callingsV2Config', v)
  }
  const saveMemberList = async (v) => {
    setMemberList(v)
    if (db) await db.set('memberList', v)
  }
  const saveSacramentTracking = async (v) => {
    setSacramentTracking(v)
    if (db) await db.set('sacramentTracking', v)
  }
  const saveInterviews = async (v) => {
    setInterviews(v)
    if (db) await db.set('interviews', v)
  }
  const saveOrgTodos = async (v) => {
    setOrgTodos(v)
    if (db) await db.set('todos', v)
  }
  const saveOrgActionItems = async (v) => {
    setOrgActionItems(v)
    if (db) await db.set('actionItems', v)
  }
  const saveSacramentAgendas = async (v) => {
    setSacramentAgendas(v)
    if (db) await db.set('sacramentAgendas', v)
  }
  const saveSettings = async (v) => {
    setSettings(v)
    if (db) await db.set('settings', v)
  }
  // Onboarding card controls (ward-side prefs on the settings blob).
  const openOnboarding = (stepId?: string) => {
    setOnboardingStepId(stepId || null)
    setOnboardingOpen(true)
  }
  const dismissOnboarding = () => saveSettings({ ...settings, onboardingDismissed: true })
  const saveWardInfo = async (v) => {
    setWardInfo(v)
    if (db) await db.set('wardInfo', v)
  }
  // Phase 3: save helpers for the unified meetings + schedules blobs.
  const saveMeetingsList = async (v: any[]) => {
    setMeetingsList(v)
    if (db) await db.set('meetings', v)
  }
  const saveMeetingSchedules = async (v: any) => {
    setMeetingSchedules(v)
    if (db) await db.set('meetingSchedules', v)
  }
  const savePrayerLists = async (v: PrayerListsBlob) => {
    if (!db) return
    await savePrayerListsBlob(db, v)
  }
  const saveNotes = async (v) => {
    setNotes(v)
    if (db) await db.set('notes', v)
  }

  // Build virtual interview-scheduling assignments for exec secretaries
  // These are computed at render time (not stored) to avoid write loops
  const interviewAssignments =
    currentUserRole === 'exec_secretary' && interviews && user?.displayName
      ? interviews
          .filter((i) => !i.date)
          .map((iv) => ({
            id: `iv-${iv.id}`,
            text: `Schedule interview with ${iv.memberName}`,
            done: false,
            assignee: user.displayName,
            createdBy: 'System',
            interviewId: iv.id,
            virtual: true,
          }))
      : []

  // Refresh org users list (called after user management changes)
  const refreshOrgUsers = async () => {
    if (!organization) return
    try {
      const users = await window.apiRequest(`/organizations/${organization.id}/users`)
      setOrgUsers(users)
    } catch (e) {
      console.error('Failed to refresh users:', e)
    }
  }

  // Map Firebase auth-error codes onto friendly user-facing strings.
  // Mirrors the mapping in apps/mobile/src/screens/auth/LoginScreen.tsx —
  // keep the two in sync.
  const friendlyAuthError = (err: unknown): string => {
    const code = (err as any)?.code as string | undefined
    switch (code) {
      case 'auth/popup-closed-by-user':
      case 'auth/cancelled-popup-request':
        // User closed the Google popup — not an error worth showing.
        return ''
      case 'auth/popup-blocked':
        return 'Your browser blocked the sign-in popup. Allow popups for this site and try again.'
      case 'auth/invalid-email':
        return "That doesn't look like a valid email address."
      case 'auth/user-disabled':
        return 'This account has been disabled.'
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
      case 'auth/invalid-login-credentials':
        return 'The email or password is incorrect.'
      case 'auth/account-exists-with-different-credential':
        return "This email is already linked to a different sign-in method. Try 'Continue with Google'."
      case 'auth/too-many-requests':
        return 'Too many attempts — try again in a few minutes.'
      case 'auth/network-request-failed':
        return 'Network error — check your connection and try again.'
      // Almost never the user's password: the Google sign-in window was
      // blocked — typically a link opened inside another app (Facebook,
      // Messages, Gmail), strict privacy settings, or an ad blocker.
      case 'auth/internal-error':
      case 'auth/web-storage-unsupported':
      case 'auth/operation-not-supported-in-this-environment':
        return "Sign-in couldn't finish in this browser. Open this site directly in Safari or Chrome (not from a link inside another app), or turn off any ad blocker, then try again."
      default:
        return (err as any)?.message || 'Sign-in failed. Please try again.'
    }
  }

  const handleGoogleSignIn = async () => {
    try {
      setAuthError('')
      await window.signInWithPopup(window.firebaseAuth, new window.GoogleAuthProvider())
    } catch (e) {
      setAuthError(friendlyAuthError(e))
    }
  }
  const handleEmailSignIn = async (email: string, password: string) => {
    try {
      setAuthError('')
      await window.signInWithEmailAndPassword(
        window.firebaseAuth,
        email.trim().toLowerCase(),
        password,
      )
    } catch (e) {
      setAuthError(friendlyAuthError(e))
    }
  }
  // Doubles as the set-your-first-password flow: invited users get an
  // Auth account auto-created at invite time (random unknown password),
  // so the reset email is how they choose their real one. Returns
  // whether the email was sent so LandingPage can show its notice.
  const handlePasswordReset = async (email: string): Promise<boolean> => {
    try {
      setAuthError('')
      await window.sendPasswordResetEmail(window.firebaseAuth, email.trim().toLowerCase())
      return true
    } catch (e) {
      setAuthError(friendlyAuthError(e))
      return false
    }
  }
  const handleSignOut = () => window.signOut(window.firebaseAuth)

  // Reset every piece of state that's populated from Firestore
  // subscriptions, back to the initial-mount defaults. Called when the
  // user switches orgs or signs out — without this, the new org would
  // briefly (or sometimes permanently) see the previous org's data
  // because db.ts's subscribe callback is gated on `doc.exists()`. If
  // the new org doesn't have a given doc yet, the callback never fires
  // and the stale array sticks. Worst case (observed in production on
  // 2026-05-17): the next save then persisted the previous org's
  // meetings into the new org's `meetings` doc, leaving cross-org
  // copies of 150+ rows in Firestore.
  const resetOrgScopedState = () => {
    setAgendas({ bishopric: [], wardCouncil: [] })
    setCallings([])
    setMemberList([])
    setSacramentTracking([])
    setInterviews([])
    setOrgTodos([])
    setSacramentAgendas([])
    setSettings({})
    setWardInfo({})
    setFetchedHymns(null)
    setCustomHymns([])
    setMeetingsList([])
    setMeetingSchedules({})
    setMeetingTemplates({ templates: [] })
    setNotes([])
  }

  // "Switch Organization" — drops the user back to OrgSelectScreen
  // without signing them out of Firebase. Mirrors the cleanup the
  // sign-out path does, *except* we keep `user` and the auth session
  // intact. We also clear the saved-per-UID preference, otherwise
  // OrgSelectScreen would auto-restore the same org we just left and
  // the button would feel broken. Tab state is reset to 'home' so a
  // returning pick doesn't land on a stale tab from the previous org.
  const handleSwitchOrg = () => {
    setOrganization(null)
    setDb(null)
    setOrgUsers([])
    setCurrentUserRole(null)
    for (const unsub of subscriptions) unsub()
    setSubscriptions([])
    resetOrgScopedState()
    setActiveTab('home')
    setAuthError('')
    if (user?.uid) clearSavedOrgId(user.uid)
  }

  // Called by <OrgSelectScreen/> once the user picks (or
  // auto-selects) an organization. This is where the per-org wiring
  // happens: link the account, fetch the user roster to determine
  // role, build the org's Firestore handle, and start the realtime
  // listeners. Pulled out of the auth listener so the picker can sit
  // between auth-success and org-loaded.
  //
  // `totalOrgs` is the count of orgs the user can access, reported by
  // OrgSelectScreen alongside the picked org. Used downstream to
  // decide whether to show the Switch Organization button — there's
  // no point offering a switch when there's only one option.
  const handleOrgSelected = async (org, totalOrgs) => {
    if (!user) return
    setLoading(true)
    setAuthError('')
    if (typeof totalOrgs === 'number') setMyOrgCount(totalOrgs)
    try {
      // Link account (idempotent — moves email-keyed doc to UID-keyed
      // doc). May 404 for super admins who aren't in the org's users
      // list, which is fine.
      try {
        await window.apiRequest(`/organizations/${org.id}/users/link`, { method: 'POST' })
      } catch (linkErr) {
        console.log('Link skipped (may be super admin):', linkErr.message)
      }
      const users = await window.apiRequest(`/organizations/${org.id}/users`)
      const me = users.find((u) => u.email === user.email.toLowerCase() || u.uid === user.uid)
      setOrgUsers(users)
      setCurrentUserRole(me?.role || (org.role === 'superadmin' ? 'superadmin' : null))
      const orgDb = createDb(org.id)
      setDb(orgDb)
      setOrganization(org)
      setupRealtimeListeners(orgDb, user)
      // Auto-seed meetingTemplates blob if missing (Phase 3). Ward and
      // stake get different starter sets (see cloneSystemTemplates).
      // db.subscribe only fires when the doc exists, so we have to detect
      // missing-blob explicitly and seed it.
      ;(async () => {
        try {
          // exists() THROWS on a read error (unlike get(), which returns null
          // for BOTH a missing doc AND a failed read — that conflation let a
          // transient read blip seed defaults over real templates). Seed ONLY
          // when the doc is definitively missing; any read error falls to the
          // catch below and skips seeding, so existing data is never clobbered.
          const hasTemplates = await orgDb.exists('meetingTemplates')
          if (!hasTemplates) {
            const orgType = (org.type === 'ward' ? 'ward' : 'stake') as 'stake' | 'ward'
            const { cloneSystemTemplates } = await import('./stake/lib/templates')
            await orgDb.set('meetingTemplates', cloneSystemTemplates(orgType))
          }
        } catch (e) {
          console.error('meetingTemplates existence check failed; skipping auto-seed to avoid clobbering data:', e)
        }
      })()
    } catch (err) {
      console.error('Failed to load organization:', err)
      setAuthError(`Could not open ${org.name}. Please try again.`)
    } finally {
      setLoading(false)
    }
  }

  // Phase 3: ward-flavored leader options for typeahead in the unified
  // meetings tab (matches the stake's stakeLeaderOptions shape:
  // [{name, role, email}]). Must be declared BEFORE any early returns
  // so the hook order is stable across renders (React error #310).
  const wardLeaderOptions = React.useMemo(() => {
    const seen = new Set<string>()
    const out: Array<{ name: string; role: string; email: string }> = []
    for (const u of orgUsers || []) {
      if (!u.displayName) continue
      const key = u.displayName.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)
      out.push({
        name: u.displayName,
        role: ROLE_DISPLAY_NAMES[u.role] || u.role || 'Member',
        email: u.email || '',
      })
    }
    return out.sort((a, b) => a.name.localeCompare(b.name))
  }, [orgUsers])

  // This ward's departing/returning missionaries, drawn from the stake's
  // (PII-stripped) wardMissionaries projection and filtered to the active ward.
  // Feeds the calendar depart/return events on the ward dashboard + meetings
  // tab, mirroring how they appear on stake calendars.
  const wardMissionaries = React.useMemo(
    () => stakeWardMissionaries.filter((m: any) => isMissionaryInWard(m?.ward, organization)),
    [stakeWardMissionaries, organization],
  )

  // ── /fast ──────────────────────────────────────────────────────────
  //
  // Ahead of every gate below, and simpler than /missionary: the fast page is
  // anonymous full stop, with no leader half to unlock, so it needs neither
  // the org picker nor the session. A signed-in leader opening the link sees
  // exactly what a member sees.
  if (isFastRoute) {
    return (
      <React.Suspense fallback={<LoadingFallback />}>
        <FastPage />
      </React.Suspense>
    )
  }

  // ── /missionary ────────────────────────────────────────────────────
  //
  // Handled ahead of the loading and signed-out gates below, because this is
  // the only route a member without an account is meant to reach: falling
  // through to LandingPage would put a sign-in wall in front of the meal
  // calendar, which is exactly what the design rules out. The page renders
  // its own loading state, and shows leader tools only once `user` +
  // `organization` + `currentUserRole` resolve. Hosting rewrites every path
  // to index.html, so no server-side route is needed.
  if (isMissionaryRoute) {
    // A signed-in leader still has to pick an org before the console can
    // render: `organization` and `currentUserRole` are set ONLY by
    // handleOrgSelected, and OrgSelectScreen is the only thing that calls it.
    // Because this branch returns above the org gate below, skipping this
    // would leave a signed-in leader stuck on the anonymous page forever —
    // `user` set, `organization` null, so MissionaryPage never sees a leader.
    //
    // Members never reach this: they have no `user`. And the picker
    // auto-selects when there's one org or a saved preference, so only
    // multi-org accounts (superadmins, mostly) actually see a screen.
    //
    // The `loading` arm matters as much as the picker: handleOrgSelected
    // flips loading back on while it fetches, and during that window
    // `organization` is still null — without this we'd drop through and
    // flash the anonymous "Nothing here yet" page mid-selection.
    if (user && !organization) {
      return loading ? (
        <LoadingFallback />
      ) : (
        <OrgSelectScreen
          user={user}
          onSelected={handleOrgSelected}
          onSignOut={handleSignOut}
        />
      )
    }
    return (
      <React.Suspense fallback={<LoadingFallback />}>
        <MissionaryPage
          user={user}
          organization={organization}
          userRole={currentUserRole}
          onSignIn={handleGoogleSignIn}
          onSignOut={handleSignOut}
          onExit={() => {
            window.history.pushState({}, '', '/')
            setIsMissionaryRoute(false)
          }}
        />
      </React.Suspense>
    )
  }

  if (loading)
    return (
      <div className="flex items-center justify-center h-screen" style={{ background: '#1e3a5f' }}>
        <div className="w-8 h-8 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
      </div>
    )

  // Signed-out → public landing page (sign-in lives in there).
  // Replaces the previous split where ward URLs got a small custom
  // sign-in card and the stake URL got the marketing landing page;
  // every visitor now lands on the same page.
  if (!user)
    return (
      <LandingPage
        onSignIn={handleGoogleSignIn}
        onEmailSignIn={handleEmailSignIn}
        onPasswordReset={handlePasswordReset}
        authError={authError}
      />
    )

  // Signed in but no org picked yet → org picker. This is the iOS
  // pattern (one screen lists every org the user belongs to,
  // auto-selects when there's only one or a saved-per-UID
  // preference exists). Single source of truth replaces the old
  // subdomain → /tenant/resolve path.
  if (!organization) {
    if (showSuperAdmin) {
      return (
        <React.Suspense fallback={<LoadingFallback />}>
          <SuperAdminPage onBack={() => setShowSuperAdmin(false)} onSignOut={handleSignOut} />
        </React.Suspense>
      )
    }
    return (
      <OrgSelectScreen
        user={user}
        onSelected={handleOrgSelected}
        onSignOut={handleSignOut}
        // OrgSelectScreen double-checks the user actually has
        // role='superadmin' on at least one org before rendering the
        // link, so handing the callback unconditionally is safe.
        onOpenSuperAdmin={() => setShowSuperAdmin(true)}
      />
    )
  }

  // Stake admin view (org.type === 'stake').
  if (isStakeView) {
    return (
      <React.Suspense fallback={<LoadingFallback />}>
        <StakeAdminView
          user={user}
          // Pass the active stake org so StakeAdminView can scope its
          // wards list and stake-data lookups to THIS stake. Without
          // this prop the legacy "first stake in the system" heuristic
          // breaks once more than one stake exists.
          organization={organization}
          onSignOut={handleSignOut}
          onSwitchOrg={canSwitchOrg ? handleSwitchOrg : undefined}
        />
      </React.Suspense>
    )
  }

  const TabIcon = ({ id }) => {
    // Design-system Ionicons per ui_kits/mobile/components.jsx.
    const TAB_IONS = {
      home: 'home-outline',
      meetings: 'clipboard-outline',
      callings: 'people-outline',
      sacrament: 'book-outline',
      assignments: 'checkmark-done-outline',
      interviews: 'calendar-outline',
      notes: 'document-text-outline',
      tracking: 'bar-chart-outline',
      settings: 'settings-outline',
      members: 'people-circle-outline',
    }
    const name = TAB_IONS[id]
    if (!name) return null
    return (
      <ion-icon
        name={name}
        style={{ fontSize: 20, color: 'currentColor', display: 'inline-block' }}
      ></ion-icon>
    )
  }
  const _tabs = [
    { id: 'home', label: 'Home' },
    { id: 'meetings', label: 'Meetings' },
    { id: 'assignments', label: 'Action items' },
    { id: 'callings', label: 'Callings' },
    { id: 'sacrament', label: 'Sacrament Meeting' },
    { id: 'interviews', label: 'Interviews' },
    { id: 'notes', label: 'Notes' },
  ]
  const _menuTabs = [
    { id: 'members', label: 'Members' },
    { id: 'tracking', label: 'Tracking' },
    { id: 'settings', label: 'Settings' },
  ]
  // Settings panel is limited to bishopric, ward clerks, and ward
  // secretaries (plus superadmins). Everyone else doesn't see the tab
  // in the sidebar, the mobile "More" menu, or the panel itself.
  const canSeeWardSettings = canEditWard(currentUserRole)
  const sidebarSecondary = [
    { id: 'tracking', label: 'Tracking' },
    { id: 'members', label: 'Members' },
    ...(canSeeWardSettings ? [{ id: 'settings', label: 'Settings' }] : []),
  ]
  const roleOrder = { bishop: 0, counselor1: 1, counselor2: 2 }
  const bishopricMembers = orgUsers
    .filter(
      (u) =>
        u.displayName && u.role !== 'exec_secretary' && u.role !== 'clerk' && u.role !== 'test',
    )
    .sort((a, b) => (roleOrder[a.role] ?? 99) - (roleOrder[b.role] ?? 99))
    .map((u) => u.displayName)
  const bishopName =
    orgUsers.find((u) => u.role === 'bishop')?.displayName || bishopricMembers[0] || ''
  // Group rosters for action-item assignee expansion. Bishopric is the
  // bishop + counselors (strict). Ward council is "everyone with login
  // access" as a pragmatic stand-in until we model auxiliary presidencies
  // explicitly — it overcounts by including the clerk and exec sec, but
  // those are real ward council attendees so it's close enough.
  const bishopricRoster = orgUsers
    .filter((u) => u.displayName && ['bishop', 'counselor1', 'counselor2'].includes(u.role))
    .sort((a, b) => (roleOrder[a.role] ?? 99) - (roleOrder[b.role] ?? 99))
    .map((u) => ({ name: u.displayName, email: u.email || '' }))
  // Interviewer dropdown roster for the Interviews tab — strictly the bishop +
  // both counselors, with role labels. (The Conducted-by list is intentionally
  // just these three; see the Interviews redesign.)
  const bishopricLeaders = orgUsers
    .filter((u) => u.displayName && ['bishop', 'counselor1', 'counselor2'].includes(u.role))
    .sort((a, b) => (roleOrder[a.role] ?? 99) - (roleOrder[b.role] ?? 99))
    .map((u) => ({ name: u.displayName, role: ROLE_DISPLAY_NAMES[u.role] || u.role }))
  const wardCouncilRoster = orgUsers
    .filter((u) => u.displayName && u.role !== 'test')
    .map((u) => ({ name: u.displayName, email: u.email || '' }))

  return (
    <div className="min-h-screen overflow-x-hidden">
      <PullToRefreshIndicator pulling={pulling} pullDistance={pullDistance} threshold={threshold} />

      {/* Desktop Sidebar — shared dark rail (parity with stake nav). */}
      <AppSidebar
        brand={formatWardBrand(organization?.name)}
        dashboard={WARD_DASHBOARD}
        sections={WARD_SECTIONS}
        settings={WARD_SETTINGS}
        activeTab={activeTab}
        navigateTo={navigateTo}
        collapsed={sidebarCollapsed}
        onToggleCollapsed={toggleSidebarCollapsed}
        showSettings={canSeeWardSettings}
      />

      {/* Desktop Header Bar — right of sidebar, hidden below lg */}
      <div
        className={`hidden lg:flex items-center justify-end h-16 bg-white border-b border-slate-200 sticky top-0 z-20 px-6 transition-all duration-200 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-[240px]'}`}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-ocean-150 text-ocean-600 flex items-center justify-center text-sm font-bold flex-shrink-0">
            {(user.displayName || user.email || '?')[0].toUpperCase()}
          </div>
          <span className="text-sm font-medium text-slate-700">
            {user.displayName || user.email}
          </span>
          {canSwitchOrg && (
            <button
              onClick={handleSwitchOrg}
              className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100"
              title="Switch Organization"
            >
              <ion-icon
                name="swap-horizontal-outline"
                style={{ fontSize: 20, color: 'currentColor' }}
              ></ion-icon>
            </button>
          )}
          <button
            onClick={handleSignOut}
            className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100"
            title="Sign Out"
          >
            <ion-icon
              name="log-out-outline"
              style={{ fontSize: 20, color: 'currentColor' }}
            ></ion-icon>
          </button>
        </div>
      </div>

      {/* Mobile Header — clean white, hidden on lg+ */}
      <header className="lg:hidden bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="px-4 sm:px-6 flex items-center justify-between h-14">
          <h1 className="text-base font-bold text-slate-900 truncate">
            {organization?.name || 'Bishopric'}
          </h1>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-7 h-7 rounded-full bg-ocean-150 text-ocean-600 flex items-center justify-center text-xs font-bold">
              {(user.displayName || user.email || '?')[0].toUpperCase()}
            </div>
            {canSwitchOrg && (
              <button
                onClick={handleSwitchOrg}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100"
                title="Switch Organization"
              >
                <ion-icon
                  name="swap-horizontal-outline"
                  style={{ fontSize: 20, color: 'currentColor' }}
                ></ion-icon>
              </button>
            )}
            <button
              onClick={handleSignOut}
              className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100"
              title="Sign Out"
            >
              <ion-icon
                name="log-out-outline"
                style={{ fontSize: 20, color: 'currentColor' }}
              ></ion-icon>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Bottom Tab Bar — floating glass capsule, hidden on lg+ */}
      <div className="lg:hidden">
        {/* More menu popover — above the tab bar, appears when Menu tab clicked */}
        {menuOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
            <div
              className="fixed left-4 right-4 z-50 bg-white border border-slate-200 rounded-2xl shadow-xl py-2"
              style={{ bottom: 'calc(92px + env(safe-area-inset-bottom, 0px))' }}
            >
              {[
                { id: 'assignments', label: 'Action items' },
                { id: 'interviews', label: 'Interviews' },
                ...sidebarSecondary,
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => navigateTo(tab.id)}
                  className={`w-full px-4 py-3 text-left flex items-center gap-3 text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-ocean-100 text-ocean-500' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                >
                  <TabIcon id={tab.id} />
                  {tab.label}
                </button>
              ))}
            </div>
          </>
        )}
        <FloatingTabBar
          tabs={[
            {
              key: 'home',
              label: 'Home',
              inactiveIcon: (
                <ion-icon
                  name="home-outline"
                  style={{ fontSize: 22, color: 'currentColor' }}
                ></ion-icon>
              ),
              activeIcon: (
                <ion-icon name="home" style={{ fontSize: 22, color: 'currentColor' }}></ion-icon>
              ),
            },
            {
              key: 'callings',
              label: 'Callings',
              inactiveIcon: (
                <ion-icon
                  name="people-outline"
                  style={{ fontSize: 22, color: 'currentColor' }}
                ></ion-icon>
              ),
              activeIcon: (
                <ion-icon name="people" style={{ fontSize: 22, color: 'currentColor' }}></ion-icon>
              ),
            },
            {
              key: 'sacrament',
              label: 'Sacrament',
              inactiveIcon: (
                <ion-icon
                  name="book-outline"
                  style={{ fontSize: 22, color: 'currentColor' }}
                ></ion-icon>
              ),
              activeIcon: (
                <ion-icon name="book" style={{ fontSize: 22, color: 'currentColor' }}></ion-icon>
              ),
            },
            {
              key: 'meetings',
              label: 'Meetings',
              inactiveIcon: (
                <ion-icon
                  name="clipboard-outline"
                  style={{ fontSize: 22, color: 'currentColor' }}
                ></ion-icon>
              ),
              activeIcon: (
                <ion-icon
                  name="clipboard"
                  style={{ fontSize: 22, color: 'currentColor' }}
                ></ion-icon>
              ),
            },
            {
              key: '__menu__',
              label: 'More',
              inactiveIcon: (
                <ion-icon
                  name="ellipsis-horizontal-outline"
                  style={{ fontSize: 22, color: 'currentColor' }}
                ></ion-icon>
              ),
              activeIcon: (
                <ion-icon
                  name="ellipsis-horizontal"
                  style={{ fontSize: 22, color: 'currentColor' }}
                ></ion-icon>
              ),
            },
          ]}
          activeKey={
            menuOpen ||
            [...sidebarSecondary, { id: 'interviews' }, { id: 'assignments' }].some(
              (t) => t.id === activeTab,
            )
              ? '__menu__'
              : activeTab
          }
          onChange={(key) => {
            if (key === '__menu__') {
              setMenuOpen(!menuOpen)
            } else {
              setMenuOpen(false)
              navigateTo(key)
            }
          }}
        />
      </div>

      {/* Main content — offset for sidebar on desktop, bottom padding for mobile tab bar */}
      <main
        className={`px-4 sm:px-6 lg:px-8 py-6 pb-24 lg:pb-6 overflow-x-hidden transition-all duration-200 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-[240px]'}`}
      >
        <div className="max-w-6xl mx-auto lg:max-w-none">
          {currentUserRole === 'test' && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2">
              <ion-icon
                name="warning-outline"
                className="text-amber-500 flex-shrink-0"
                style={{ fontSize: 20, color: 'currentColor' }}
              ></ion-icon>
              <span className="text-sm text-amber-800 font-medium">Test Account — Read Only</span>
            </div>
          )}
          <React.Suspense fallback={<LoadingFallback />}>
            {activeTab === 'home' && (
              <WardDashboard
                user={user}
                organization={organization}
                meetingsList={meetingsList}
                meetingSchedules={meetingSchedules}
                bishopricMembers={bishopricMembers}
                bishopricRoster={bishopricRoster}
                wardCouncilRoster={wardCouncilRoster}
                sacramentAgendas={sacramentAgendas}
                wardInfo={wardInfo}
                // This ward's departing/returning missionaries (from the stake's
                // wardMissionaries projection) — surfaced in the Upcoming
                // meetings card as depart/return events.
                missionaries={wardMissionaries}
                // Stake meetings flagged "Show up on ward calendars" — mixed
                // into the Upcoming meetings card with a Stake badge, same as
                // the ward Meetings page.
                stakeSharedProjection={stakeSharedMeetings}
                timeZone={(settings as { timeZone?: string }).timeZone}
                actionItems={orgActionItems}
                saveActionItems={saveOrgActionItems}
                wardLeaderOptions={wardLeaderOptions}
                callingWorkflowsV2={callingWorkflowsV2}
                callingsV2Config={callingsV2Config}
                currentUserRole={currentUserRole}
                navigateTo={navigateTo}
                onboardingEnabled={onboardingEnabled}
                settings={settings}
                memberCount={memberList.length}
                hasBishop={orgUsers.some((u) => u.role === 'bishop')}
                openOnboarding={openOnboarding}
                dismissOnboarding={dismissOnboarding}
              />
            )}
            {activeTab === 'assignments' && (
              <WardActionItemsView
                actionItems={orgActionItems}
                saveActionItems={saveOrgActionItems}
                bishopricMembers={bishopricRoster}
                wardCouncilMembers={wardCouncilRoster}
                wardMeetings={meetingsList}
                user={user}
                wardLeaderOptions={wardLeaderOptions}
                canEdit={canEditWard(currentUserRole) || currentUserRole === 'superadmin'}
              />
            )}
            {activeTab === 'meetings' && (
              // The MeetingsTab component is org-agnostic. For ward we feed
              // it `orgData.actionItems` through the `stakeData` prop (the
              // only field the carry-forward + per-meeting logic reads
              // from that blob is `actionItems`, plus the group rosters
              // used by the assignee picker). `saveStakeData` extracts
              // `actionItems` and persists it through the ward data
              // pipeline. Group expansion for `All Bishopric Members` /
              // `All Ward Council Members` reads `bishopricMembers` and
              // `wardCouncilMembers` from the same blob.
              <MeetingsTab
                user={user}
                stakeOrgId={organization?.id}
                orgType="ward"
                stakeMeetings={meetingsList}
                saveStakeMeetings={saveMeetingsList}
                setStakeMeetingsLocal={setMeetingsList}
                stakeMeetingSchedules={meetingSchedules}
                saveStakeMeetingSchedules={saveMeetingSchedules}
                stakePresidencyMembers={bishopricMembers}
                // This ward's departing/returning missionaries (from the stake's
                // wardMissionaries projection) — surfaced as calendar events.
                missionaries={wardMissionaries}
                stakeData={{
                  actionItems: orgActionItems,
                  bishopricMembers: bishopricRoster,
                  wardCouncilMembers: wardCouncilRoster,
                  // Mix ward sacrament meetings into the meetings calendar
                  // as 'sacrament' events. wardInfo gives the conducting-
                  // schedule lookup + default sacrament start time.
                  sacramentAgendas,
                  wardInfo,
                }}
                saveStakeData={async (next) => {
                  await saveOrgActionItems(next?.actionItems || [])
                }}
                meetingTemplates={meetingTemplates}
                prayerLists={prayerLists}
                savePrayerLists={savePrayerLists}
                currentStakeUserRole={currentUserRole}
                stakeLeaderOptions={wardLeaderOptions}
                canEdit={canEditWard(currentUserRole) || currentUserRole === 'superadmin'}
                // Ward tz lives in the ward Settings blob; passing through so
                // recurrence "today" + the schedule editor's preview footer
                // anchor to the ward's clock instead of the viewer's. Cast
                // here because `settings` is loose-typed as `{}` from
                // useState({}) — proper Settings typing is a separate cleanup.
                timeZone={(settings as { timeZone?: string }).timeZone}
                // Deep-link from the dashboard's Upcoming meetings card.
                initialMeetingId={pendingMeetingId}
                onInitialMeetingConsumed={() => setPendingMeetingId(null)}
                // Deep-link from the onboarding wizard straight into a
                // schedule's recurrence/time editor.
                initialScheduleKey={pendingScheduleKey}
                onInitialScheduleConsumed={() => setPendingScheduleKey(null)}
                navListNonce={navListNonce}
                // Stake meetings flagged to also appear on ward pages —
                // merged into the list read-only with a "Stake" badge.
                stakeSharedProjection={stakeSharedMeetings}
              />
            )}
            {activeTab === 'callings' && (
              // 2026-05 cutover: the Callings tab now renders the V2 redesign.
              // The legacy WardCallingsView component stays on disk (unwired)
              // so we can revert quickly if production turns up a regression.
              <WardCallingsV2View
                callingWorkflowsV2={callingWorkflowsV2}
                saveCallingWorkflowsV2={saveCallingWorkflowsV2}
                callingsV2Config={callingsV2Config}
                wardLeaderOptions={wardLeaderOptions}
                currentUserName={user?.displayName || user?.email || ''}
                currentUserRole={currentUserRole}
                canEdit={canEditWard(currentUserRole) || currentUserRole === 'superadmin'}
                navListNonce={navListNonce}
              />
            )}
            {activeTab === 'ordinations' && (
              // Aaronic Priesthood ordinations tracked by the ward
              // (deacon/teacher/priest — editable, shares the ward's
              // callingWorkflowsV2 blob with the Callings tab), plus the
              // read-only slice of the stake's in-progress Melchizedek
              // ordinations below. workflow.ward on the stake projection
              // matches StakeWardInfo.unit, which in practice holds either
              // the ward's subdomain code (d1/jll) or its full name —
              // match both, case-insensitively.
              <WardAaronicOrdinationsView
                callingWorkflowsV2={callingWorkflowsV2}
                saveCallingWorkflowsV2={saveCallingWorkflowsV2}
                callingsV2Config={callingsV2Config}
                wardLeaderOptions={wardLeaderOptions}
                currentUserName={user?.displayName || user?.email || ''}
                currentUserRole={currentUserRole}
                canEdit={canEditWard(currentUserRole) || currentUserRole === 'superadmin'}
                navListNonce={navListNonce}
                stakeSection={
                  <WardOrdinationsView
                    embedded
                    wardLabel={organization?.name}
                    ordinations={stakeWardOrdinations.filter((o: any) => {
                      const w = (o?.ward || '').trim().toLowerCase()
                      if (!w) return false
                      const sub = (organization?.subdomain || '').trim().toLowerCase()
                      const name = (organization?.name || '').trim().toLowerCase()
                      return (!!sub && w === sub) || (!!name && w === name)
                    })}
                  />
                }
              />
            )}
            {activeTab === 'sacrament' && (
              <SacramentTab
                agendas={sacramentAgendas}
                save={saveSacramentAgendas}
                members={memberList}
                callings={callings}
                callingWorkflowsV2={callingWorkflowsV2}
                hymns={[...(fetchedHymns || allHymns), ...stakeCustomHymns].sort(
                  (a, b) => a.number - b.number,
                )}
                tracking={sacramentTracking}
                saveTracking={saveSacramentTracking}
                bishopricMembers={bishopricMembers}
                bishopName={bishopName}
                settings={settings}
                orgName={organization?.name || 'Bishopric'}
                wardInfo={wardInfo}
                currentUserName={user?.displayName || user?.email || ''}
                // Deep-link from the dashboard's sacrament meeting card or
                // the upcoming meetings list — opens the matching week's
                // agenda viewer on mount.
                initialAgendaId={pendingSacramentId}
                onInitialAgendaConsumed={() => setPendingSacramentId(null)}
                navListNonce={navListNonce}
              />
            )}
            {activeTab === 'members' && (
              <MembersTab
                members={memberList}
                saveMembers={saveMemberList}
                tracking={sacramentTracking}
                saveTracking={saveSacramentTracking}
              />
            )}
            {activeTab === 'tracking' && (
              <TrackingTab
                tracking={sacramentTracking}
                save={saveSacramentTracking}
                members={memberList}
                saveMembers={saveMemberList}
                agendas={sacramentAgendas}
              />
            )}
            {activeTab === 'interviews' && (
              <InterviewsTab
                interviews={interviews}
                save={saveInterviews}
                members={memberList}
                bishopricLeaders={bishopricLeaders}
                userName={user?.displayName || ''}
                userEmail={user?.email}
                userRole={currentUserRole}
                wardInfo={wardInfo}
                timeZone={(settings as { timeZone?: string }).timeZone}
              />
            )}
            {activeTab === 'notes' && (
              <NotesTab
                notes={notes}
                save={saveNotes}
                userDisplayName={user?.displayName}
                navListNonce={navListNonce}
              />
            )}
            {activeTab === 'settings' && canSeeWardSettings && (
              <SettingsTab
                settings={settings}
                save={saveSettings}
                orgUsers={orgUsers}
                organization={organization}
                currentUserRole={currentUserRole}
                refreshOrgUsers={refreshOrgUsers}
                onboardingEnabled={onboardingEnabled}
                fetchedHymns={fetchedHymns}
                saveFetchedHymns={async (v) => {
                  setFetchedHymns(v)
                  if (db) await db.set('hymns', v)
                }}
                customHymns={customHymns}
                saveCustomHymns={async (v) => {
                  setCustomHymns(v)
                  if (db) await db.set('customHymns', v)
                }}
                stakeCustomHymns={stakeCustomHymns}
                // Phase 3: legacy "Meeting Tab Order" section deleted in
                // favour of Meeting Templates. Old props kept for back-compat
                // in case a plugin/extension reads them; safe no-ops.
                meetings={{}}
                saveMeetings={() => {}}
                meetingTemplates={meetingTemplates}
                saveMeetingTemplates={async (v: any) => {
                  setMeetingTemplates(v)
                  if (db) await db.set('meetingTemplates', v)
                }}
                meetingSchedules={meetingSchedules}
                prayerLists={prayerLists}
                savePrayerLists={savePrayerLists}
                todos={orgTodos}
                saveTodos={saveOrgTodos}
                callingsV2Config={callingsV2Config}
                saveCallingsV2Config={saveCallingsV2Config}
                wardInfo={wardInfo}
                saveWardInfo={saveWardInfo}
                bishopricMembers={bishopricMembers}
              />
            )}
          </React.Suspense>
          {organization?.type === 'ward' && (
            <React.Suspense fallback={null}>
              <WardOnboardingWizard
                open={onboardingOpen}
                stepId={onboardingStepId as any}
                onClose={() => setOnboardingOpen(false)}
                organization={organization}
                settings={settings}
                saveSettings={saveSettings}
                orgUsers={orgUsers}
                refreshOrgUsers={refreshOrgUsers}
                wardInfo={wardInfo}
                saveWardInfo={saveWardInfo}
                meetingSchedules={meetingSchedules}
                members={memberList}
                saveMembers={saveMemberList}
                tracking={sacramentTracking}
                saveTracking={saveSacramentTracking}
                navigateTo={navigateTo}
              />
            </React.Suspense>
          )}
        </div>
      </main>
    </div>
  )
}

// ============================================================
// Stake Callings Reusable Components
// ============================================================

// Helper: display ward subdomain nicely (uppercase)

const root = ReactDOM.createRoot(document.getElementById('root'))
root.render(
  <ErrorBoundary>
    <ConnectionBanner />
    <App />
  </ErrorBoundary>,
)
